
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { NavHeader } from "@/components/layout/nav-header";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "@/hooks/use-toast";

interface User {
  id: number;
  email: string;
  displayName: string | null;
  type: string;
  roles: string[];
  // Add other fields if necessary based on API response
}

export default function AdminUsers() {
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [sortField, setSortField] = useState<'email' | 'roles' | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [searchTerm, setSearchTerm] = useState('');
  const queryClient = useQueryClient();

  const { data: users = [] } = useQuery<User[]>({
    queryKey: ["admin-users"],
    queryFn: async (): Promise<User[]> => {
      const res = await fetch("/api/admin/users");
      if (!res.ok) throw new Error("Failed to fetch users");
      return res.json();
    },
  });

  const deleteUserMutation = useMutation({
    mutationFn: async (userId: number) => {
      const res = await fetch(`/api/admin/users/${userId}`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error("Failed to delete user");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      toast({ title: "User deleted successfully" });
    },
    onError: (error) => {
      toast({ 
        title: "Error deleting user",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  const updateUserMutation = useMutation<User, Error, User>({
    mutationFn: async (userData: User) => {
      const res = await fetch(`/api/admin/users/${userData.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(userData),
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || "Failed to update user");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      toast({ title: "User updated successfully" });
      setEditingUser(null);
    },
    onError: (error) => {
      toast({ 
        title: "Error updating user",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  const handleSaveUser = (user: User) => {
    updateUserMutation.mutate(user);
  };

  return (
    <div className="min-h-screen bg-background">
      <NavHeader />
      <main className="container mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold mb-6">Manage Users</h1>
        <div className="mb-4">
          <Input
            placeholder="Search by email or display name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="max-w-md"
          />
        </div>
        <div className="bg-card rounded-lg shadow">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead 
                  className="cursor-pointer hover:bg-muted"
                  onClick={() => {
                    setSortDirection(sortField === 'email' && sortDirection === 'asc' ? 'desc' : 'asc');
                    setSortField('email');
                  }}
                >
                  Email {sortField === 'email' && (sortDirection === 'asc' ? '↑' : '↓')}
                </TableHead>
                <TableHead>Display Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-muted"
                  onClick={() => {
                    setSortDirection(sortField === 'roles' && sortDirection === 'asc' ? 'desc' : 'asc');
                    setSortField('roles');
                  }}
                >
                  Roles {sortField === 'roles' && (sortDirection === 'asc' ? '↑' : '↓')}
                </TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users
                .filter((user: User) => 
                  searchTerm === '' || 
                  user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  (user.displayName || '').toLowerCase().includes(searchTerm.toLowerCase())
                )
                .sort((a: User, b: User) => {
                  if (sortField === 'email') {
                    return sortDirection === 'asc' 
                      ? a.email.localeCompare(b.email)
                      : b.email.localeCompare(a.email);
                  }
                  if (sortField === 'roles') {
                    return sortDirection === 'asc'
                      ? a.roles.join(',').localeCompare(b.roles.join(','))
                      : b.roles.join(',').localeCompare(a.roles.join(','));
                  }
                  return 0;
                })
                .map((user) => (
                <TableRow key={(user as User).id}>
                  <TableCell>{(user as User).email}</TableCell>
                  <TableCell>{(user as User).displayName}</TableCell>
                  <TableCell>{(user as User).type}</TableCell>
                  <TableCell>{(user as User).roles.join(", ")}</TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEditingUser(user as User)}
                      >
                        Edit
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="destructive" size="sm">
                            Delete
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete User</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete this user? This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => deleteUserMutation.mutate((user as User).id)}>
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <Dialog open={!!editingUser} onOpenChange={() => setEditingUser(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit User</DialogTitle>
            </DialogHeader>
            {editingUser && (
              <div className="space-y-4 py-4">
                <div>
                  <label className="text-sm font-medium">Email</label>
                  <Input
                    value={editingUser?.email || ""}
                    onChange={(e) =>
                      editingUser && setEditingUser({ ...editingUser, email: e.target.value })
                    }
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Display Name</label>
                  <Input
                    value={editingUser?.displayName || ""}
                    onChange={(e) =>
                      editingUser && setEditingUser({
                        ...editingUser,
                        displayName: e.target.value,
                      })
                    }
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Roles</label>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        checked={editingUser?.roles.includes("admin") || false}
                        onCheckedChange={(checked) => {
                          if (!editingUser) return;
                          const newRoles = checked
                            ? [...editingUser.roles, "admin"]
                            : editingUser.roles.filter((r: string) => r !== "admin");
                          setEditingUser({ ...editingUser, roles: newRoles });
                        }}
                      />
                      <label>Admin</label>
                    </div>
                  </div>
                </div>
                <Button
                  className="w-full"
                  onClick={() => handleSaveUser(editingUser)}
                >
                  Save Changes
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
